import { FormEvent, useEffect, useState } from 'react';
import { portalApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AccessLevel, ModuleDef } from '../lib/permissions';

interface Role {
  id: string; key: string; name: string; description: string | null;
  isSystem: boolean; isSuperadmin: boolean; userCount: number;
  permissions: Record<string, AccessLevel> | 'all';
}

export default function Roles() {
  const { can } = useAuth();
  const canWrite = can('portal_users', 'write');

  const [roles, setRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<ModuleDef[]>([]);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  /** Compact one-line summary shown on the collapsed card. */
  function summarise(perms: Role['permissions']): string {
    if (perms === 'all') return 'every module · full access';
    const vals = Object.values(perms);
    const w = vals.filter((v) => v === 'write').length;
    const r = vals.filter((v) => v === 'read').length;
    if (!w && !r) return 'no module access';
    return [w ? `${w} write` : '', r ? `${r} read` : ''].filter(Boolean).join(' · ');
  }

  async function load() {
    try {
      const [r, cat] = await Promise.all([
        portalApi.get<Role[]>('/roles'),
        portalApi.get<{ modules: ModuleDef[] }>('/roles/catalogue'),
      ]);
      setRoles(r); setModules(cat.modules);
    } catch (err: any) { setError(err.message); }
  }
  useEffect(() => { void load(); }, []);

  async function setLevel(role: Role, moduleKey: string, level: AccessLevel) {
    if (role.permissions === 'all') return;
    setError('');
    // Only send modules that exist in the current catalogue. A role loaded from
    // the DB may still carry a grant for a retired module (e.g. company_map);
    // echoing it back would be rejected — and the editor only edits catalogue modules.
    const known = new Set(modules.map((m) => m.key));
    const next: Record<string, AccessLevel> = { [moduleKey]: level };
    for (const [k, v] of Object.entries(role.permissions)) {
      if (known.has(k) && k !== moduleKey) next[k] = v;
    }
    try {
      await portalApi.put(`/roles/${role.id}/permissions`, { permissions: next });
      await load();
    } catch (err: any) { setError(err.message); }
  }

  async function remove(role: Role) {
    setError('');
    try {
      await portalApi.delete(`/roles/${role.id}`);
      await load();
    } catch (err: any) { setError(err.message); }
  }

  return (
    <>
      <header className="page-head">
        <h1>Roles</h1>
        <p className="muted">
          Modules are fixed; roles are yours to define. Create as many as you need.
        </p>
        {canWrite && (
          <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New role'}
          </button>
        )}
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {creating && <CreateRole onDone={() => { setCreating(false); void load(); }} />}

      {roles.map((role) => {
        const open = expanded.has(role.id);
        return (
        <div className="card" key={role.id}>
          <div className="role-head">
            <button type="button" className="role-toggle" aria-expanded={open} onClick={() => toggle(role.id)}>
              <span className={'chevron' + (open ? ' open' : '')}>▸</span>
              <span className="role-toggle-body">
                <span className="card-title" style={{ margin: 0 }}>
                  {role.name}
                  {role.isSystem && <span className="tag">built-in</span>}
                  {role.isSuperadmin && <span className="tag tag-hot">full access</span>}
                </span>
                <span className="muted small block">
                  <code>{role.key}</code> · {role.userCount} user{role.userCount === 1 ? '' : 's'} · {summarise(role.permissions)}
                  {role.description ? ` · ${role.description}` : ''}
                </span>
              </span>
            </button>
            {canWrite && !role.isSystem && role.userCount === 0 && (
              <button className="btn btn-sm btn-danger" onClick={() => remove(role)}>Delete</button>
            )}
          </div>

          {open && (role.permissions === 'all' ? (
            <p className="muted small" style={{ marginTop: 12 }}>
              Superadmin always has every module, including ones added later. Not editable.
            </p>
          ) : (
            <div className="perm-grid" style={{ marginTop: 12 }}>
              {modules.map((m) => {
                const held = (role.permissions as Record<string, AccessLevel>)[m.key] ?? 'none';
                return (
                  <div className="perm-row" key={m.key}>
                    <div className="perm-name">
                      {m.name}
                      {m.tier === 'critical' && <span className="dot dot-hot" title="critical" />}
                      <span className="muted small block">{m.description}</span>
                    </div>
                    <div className="seg">
                      {(['none', 'read', 'write'] as AccessLevel[]).map((lvl) => {
                        const disabled = !canWrite || (lvl === 'write' && m.readOnly);
                        return (
                          <button
                            key={lvl}
                            type="button"
                            disabled={disabled}
                            title={lvl === 'write' && m.readOnly ? 'This module is read-only' : undefined}
                            className={'seg-btn' + (held === lvl ? ' on' : '')}
                            onClick={() => setLevel(role, m.key, lvl)}
                          >
                            {lvl}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        );
      })}
    </>
  );
}

function CreateRole({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({ key: '', name: '', description: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await portalApi.post('/roles', {
        key: form.key, name: form.name,
        description: form.description || undefined,
      });
      onDone();
    } catch (err: any) {
      setError(Array.isArray(err.body?.message) ? err.body.message.join(', ') : err.message);
    } finally { setBusy(false); }
  }

  return (
    <form className="card form-grid" onSubmit={submit}>
      <h2 className="card-title">New role</h2>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="grid-2">
        <label className="field"><span>Display name</span>
          <input required placeholder="Content Manager" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="field">
          <span>Key — permanent, lowercase</span>
          <input required pattern="[a-z][a-z0-9_]*" placeholder="content_manager" value={form.key}
            onChange={(e) => setForm({ ...form, key: e.target.value })} /></label>
      </div>
      <label className="field"><span>Description (optional)</span>
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
      <p className="muted small">Created with no access. Grant modules below once it exists.</p>
      <button className="btn btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create role'}</button>
    </form>
  );
}
